import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { AlertTriangle, CalendarIcon, FileText, Loader2 } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { InvoiceLineItems } from './InvoiceLineItems';
import { useCreateInvoice } from '@/hooks/useInvoices';
import { useJobParts } from '@/hooks/useJobParts';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { InvoiceLineItem } from '@/types/invoice';
import { buildInvoiceLineItems, calculateInvoiceTotals, partsExceedTotal } from '@/utils/invoiceFromJob';
import { partsSellTotal } from '@/utils/partsPricing';

interface Job {
  id: string;
  job_number: string;
  customer_id: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string;
  customer_address: string | null;
  customer_suburb?: string | null;
  customer_state?: string | null;
  customer_postcode?: string | null;
  work_summary?: string | null;
  invoice_total: number | null;
  total_parts_cost: number;
  toll_cost: number;
}

interface InvoiceFromJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
}

export function InvoiceFromJobDialog({ open, onOpenChange, job }: InvoiceFromJobDialogProps) {
  const navigate = useNavigate();
  const createInvoice = useCreateInvoice();
  const { data: parts = [] } = useJobParts(job.id);
  const tax = useCompanyTax();
  const { formatMoney } = useCompanyLocale();

  const [issueDate, setIssueDate] = useState<Date>(new Date());
  const [dueDate, setDueDate] = useState<Date>(addDays(new Date(), 14));
  const [notes, setNotes] = useState('');
  // Empty = fall back to the company's configured invoice terms on the invoice
  const [terms, setTerms] = useState('');

  // Parts go on the invoice at their sell price; the internal part_cost never leaves the job.
  const partsForInvoice = parts.map(p => ({
    part_name: p.part_name,
    sell_price: p.sell_price,
    quantity: p.quantity,
  }));
  const jobTotal = job.invoice_total || 0;
  const partsOverTotal = partsExceedTotal({ invoice_total: jobTotal }, partsForInvoice);

  // Build line items using shared utility
  const buildLineItems = (): InvoiceLineItem[] => {
    const items = buildInvoiceLineItems(
      { work_summary: job.work_summary, invoice_total: jobTotal },
      partsForInvoice,
      tax,
    );
    return items.length > 0 ? items : [{ description: '', quantity: 1, unit_price: 0, amount: 0 }];
  };

  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>(buildLineItems);
  // useJobParts resolves after first render: rebuild once the parts arrive, unless the
  // user has already edited the lines. Without this the parts lines were missing and
  // the invoice totalled revenue minus parts.
  const partsApplied = useRef(false);
  const [lineItemsTouched, setLineItemsTouched] = useState(false);
  useEffect(() => {
    if (partsApplied.current || lineItemsTouched || parts.length === 0) return;
    partsApplied.current = true;
    setLineItems(buildLineItems());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, lineItemsTouched]);

  // Use shared utility for totals calculation
  const { subtotal, gstAmount, totalAmount } = calculateInvoiceTotals(lineItems, tax);

  const handleCreate = async () => {
    const result = await createInvoice.mutateAsync({
      job_id: job.id,
      customer_id: job.customer_id,
      customer_name: job.customer_name,
      customer_email: job.customer_email,
      customer_phone: job.customer_phone,
      customer_address: job.customer_address,
      service_address: [
        job.customer_address,
        [job.customer_suburb, job.customer_state, job.customer_postcode].filter(Boolean).join(' '),
      ].filter(Boolean).join(', ') || null,
      subtotal,
      gst_amount: gstAmount,
      total_amount: totalAmount,
      issue_date: format(issueDate, 'yyyy-MM-dd'),
      due_date: format(dueDate, 'yyyy-MM-dd'),
      notes: notes || null,
      terms: terms || null,
      // Keep $0.00 lines — they list work included in the bulk service price
      line_items: lineItems.filter(item => item.description.trim()),
    });

    onOpenChange(false);
    navigate('/invoices');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Invoice from Job {job.job_number}
          </DialogTitle>
          <DialogDescription>
            Create an invoice pre-populated with job details. You can adjust line items before creating.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Customer Info (Read-only) */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Customer</h3>
            <p className="font-medium">{job.customer_name}</p>
            {job.customer_phone && <p className="text-sm text-muted-foreground">{job.customer_phone}</p>}
            {job.customer_email && <p className="text-sm text-muted-foreground">{job.customer_email}</p>}
            {job.customer_address && <p className="text-sm text-muted-foreground">{job.customer_address}</p>}
          </div>

          {/* Invoice Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Issue Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(issueDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={issueDate}
                    onSelect={(date) => date && setIssueDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(dueDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={(date) => date && setDueDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-4">
            <h3 className="font-semibold">Line Items</h3>
            {partsOverTotal && (
              <div className="flex gap-2 items-start rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>
                  The parts charged to the customer ({formatMoney(partsSellTotal(partsForInvoice))}) add up to more
                  than the job total ({formatMoney(jobTotal)}), so no service line was generated. Adjust the lines
                  below, or fix the job revenue / part sell prices first.
                </p>
              </div>
            )}
            <InvoiceLineItems
              items={lineItems}
              onChange={(items) => { setLineItemsTouched(true); setLineItems(items); }}
            />
          </div>

          {/* Notes & Terms */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Internal)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="terms">Payment Terms</Label>
              <Textarea
                id="terms"
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                placeholder="Leave blank to use your company's invoice terms"
                rows={3}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreate}
            disabled={lineItems.length === 0 || createInvoice.isPending}
          >
            {createInvoice.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Invoice'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
